/**
 * WGS84-only conversions between decimal degrees, DMS, and UTM.
 * Geographic order for proj4: longitude, latitude (x, y).
 */

import proj4 from "proj4";

const WGS84 = "EPSG:4326";

// --- Types ------------------------------------------------------------------

/** Decimal degrees, WGS84 (latitude −90…90, longitude −180…180). */
export type Wgs84Decimal = { lat: number; lon: number };

export type DmsHemisphereLat = "N" | "S";
export type DmsHemisphereLon = "E" | "W";

export type Wgs84DmsLegLat = {
  degrees: number;
  minutes: number;
  seconds: number;
  hemisphere: DmsHemisphereLat;
};

export type Wgs84DmsLegLon = {
  degrees: number;
  minutes: number;
  seconds: number;
  hemisphere: DmsHemisphereLon;
};

export type Wgs84Dms = {
  latitude: Wgs84DmsLegLat;
  longitude: Wgs84DmsLegLon;
};

/** WGS84 UTM: zone 1–60, easting/northing in metres. */
export type Wgs84Utm = {
  zone: number;
  hemisphere: "N" | "S";
  easting: number;
  northing: number;
};

// --- Validation -------------------------------------------------------------

export function assertWgs84Decimal(d: Wgs84Decimal): void {
  if (!Number.isFinite(d.lat) || !Number.isFinite(d.lon)) {
    throw new RangeError("Latitude and longitude must be finite numbers.");
  }
  if (d.lat < -90 || d.lat > 90) {
    throw new RangeError("Latitude must be between -90 and 90 degrees.");
  }
  if (d.lon < -180 || d.lon > 180) {
    throw new RangeError("Longitude must be between -180 and 180 degrees.");
  }
}

function assertDmsLegLat(leg: Wgs84DmsLegLat): void {
  if (
    !Number.isFinite(leg.degrees) ||
    !Number.isFinite(leg.minutes) ||
    !Number.isFinite(leg.seconds)
  ) {
    throw new RangeError("DMS latitude components must be finite numbers.");
  }
  if (leg.degrees < 0 || leg.degrees > 90) {
    throw new RangeError("DMS latitude degrees must be between 0 and 90.");
  }
  if (leg.minutes < 0 || leg.minutes >= 60) {
    throw new RangeError("DMS minutes must be in [0, 60).");
  }
  if (leg.seconds < 0 || leg.seconds >= 60) {
    throw new RangeError("DMS seconds must be in [0, 60).");
  }
  if (leg.hemisphere !== "N" && leg.hemisphere !== "S") {
    throw new RangeError("Latitude hemisphere must be N or S.");
  }
  if (leg.degrees === 90 && (leg.minutes > 0 || leg.seconds > 0)) {
    throw new RangeError("Latitude cannot exceed 90°.");
  }
}

function assertDmsLegLon(leg: Wgs84DmsLegLon): void {
  if (
    !Number.isFinite(leg.degrees) ||
    !Number.isFinite(leg.minutes) ||
    !Number.isFinite(leg.seconds)
  ) {
    throw new RangeError("DMS longitude components must be finite numbers.");
  }
  if (leg.degrees < 0 || leg.degrees > 180) {
    throw new RangeError("DMS longitude degrees must be between 0 and 180.");
  }
  if (leg.minutes < 0 || leg.minutes >= 60) {
    throw new RangeError("DMS minutes must be in [0, 60).");
  }
  if (leg.seconds < 0 || leg.seconds >= 60) {
    throw new RangeError("DMS seconds must be in [0, 60).");
  }
  if (leg.hemisphere !== "E" && leg.hemisphere !== "W") {
    throw new RangeError("Longitude hemisphere must be E or W.");
  }
  if (leg.degrees === 180 && (leg.minutes > 0 || leg.seconds > 0)) {
    throw new RangeError("Longitude cannot exceed 180°.");
  }
}

function assertUtm(u: Wgs84Utm): void {
  if (!Number.isInteger(u.zone) || u.zone < 1 || u.zone > 60) {
    throw new RangeError("UTM zone must be an integer from 1 to 60.");
  }
  if (u.hemisphere !== "N" && u.hemisphere !== "S") {
    throw new RangeError("UTM hemisphere must be N or S.");
  }
  if (!Number.isFinite(u.easting) || !Number.isFinite(u.northing)) {
    throw new RangeError("Easting and northing must be finite.");
  }
}

function epsgForUtmZone(zone: number, northern: boolean): string {
  const code = (northern ? 32600 : 32700) + zone;
  return `EPSG:${code}`;
}

/** Longitude → UTM zone (1–60), standard 6° segments. */
export function utmZoneFromLongitude(lon: number): number {
  let z = Math.floor((lon + 180) / 6) + 1;
  if (z < 1) z = 1;
  if (z > 60) z = 60;
  return z;
}

// --- Decimal ↔ DMS ----------------------------------------------------------

function decimalToDmsLeg(
  value: number,
  maxMagnitude: number,
  positiveHemisphere: "N" | "E",
  negativeHemisphere: "S" | "W",
): { degrees: number; minutes: number; seconds: number; hemisphere: string } {
  const hem = value >= 0 ? positiveHemisphere : negativeHemisphere;
  const abs = Math.min(Math.abs(value), maxMagnitude);
  const deg = Math.floor(abs);
  const minF = (abs - deg) * 60;
  const min = Math.floor(minF);
  let sec = (minF - min) * 60;
  if (sec >= 60 - 1e-12) {
    sec = 0;
  }
  return { degrees: deg, minutes: min, seconds: sec, hemisphere: hem };
}

export function wgs84DecimalToDms(d: Wgs84Decimal): Wgs84Dms {
  assertWgs84Decimal(d);
  const latParts = decimalToDmsLeg(d.lat, 90, "N", "S");
  const lonParts = decimalToDmsLeg(d.lon, 180, "E", "W");
  return {
    latitude: {
      degrees: latParts.degrees,
      minutes: latParts.minutes,
      seconds: latParts.seconds,
      hemisphere: latParts.hemisphere as DmsHemisphereLat,
    },
    longitude: {
      degrees: lonParts.degrees,
      minutes: lonParts.minutes,
      seconds: lonParts.seconds,
      hemisphere: lonParts.hemisphere as DmsHemisphereLon,
    },
  };
}

function dmsLegLatToDecimal(leg: Wgs84DmsLegLat): number {
  assertDmsLegLat(leg);
  const mag = leg.degrees + leg.minutes / 60 + leg.seconds / 3600;
  return leg.hemisphere === "S" ? -mag : mag;
}

function dmsLegLonToDecimal(leg: Wgs84DmsLegLon): number {
  assertDmsLegLon(leg);
  const mag = leg.degrees + leg.minutes / 60 + leg.seconds / 3600;
  return leg.hemisphere === "W" ? -mag : mag;
}

export function wgs84DmsToDecimal(dms: Wgs84Dms): Wgs84Decimal {
  const lat = dmsLegLatToDecimal(dms.latitude);
  const lon = dmsLegLonToDecimal(dms.longitude);
  const out = { lat, lon };
  assertWgs84Decimal(out);
  return out;
}

// --- Decimal ↔ UTM ----------------------------------------------------------

export function wgs84DecimalToUtm(d: Wgs84Decimal): Wgs84Utm {
  assertWgs84Decimal(d);
  const zone = utmZoneFromLongitude(d.lon);
  const northern = d.lat >= 0;
  const dest = epsgForUtmZone(zone, northern);
  const [easting, northing] = proj4(WGS84, dest, [d.lon, d.lat]);
  return { zone, hemisphere: northern ? "N" : "S", easting, northing };
}

export function wgs84UtmToDecimal(utm: Wgs84Utm): Wgs84Decimal {
  assertUtm(utm);
  const northern = utm.hemisphere === "N";
  const src = epsgForUtmZone(utm.zone, northern);
  const [lon, lat] = proj4(src, WGS84, [utm.easting, utm.northing]);
  const out = { lat, lon };
  assertWgs84Decimal(out);
  return out;
}

// --- Cross: DMS ↔ UTM -------------------------------------------------------

export function wgs84DmsToUtm(dms: Wgs84Dms): Wgs84Utm {
  return wgs84DecimalToUtm(wgs84DmsToDecimal(dms));
}

export function wgs84UtmToDms(utm: Wgs84Utm): Wgs84Dms {
  return wgs84DecimalToDms(wgs84UtmToDecimal(utm));
}
